import { intlLocale } from "@/lib/i18n"

type FormatMoneyOptions = {
  currency?: string
  locale?: string
}

type FormatDateTimeOptions = {
  locale?: string
  dateStyle?: Intl.DateTimeFormatOptions["dateStyle"]
  timeStyle?: Intl.DateTimeFormatOptions["timeStyle"]
  // IANA identifier of the user or location the datetime belongs to.
  // Omitting it falls back to the browser's zone.
  timeZone?: string
  options?: Intl.DateTimeFormatOptions
}

const moneyFormatters = new Map<string, Intl.NumberFormat>()
const numberFormatters = new Map<string, Intl.NumberFormat>()
const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>()

export function formatMoney(value: number | string, options: FormatMoneyOptions = {}) {
  const { currency = "MXN", locale = intlLocale() } = options
  const numeric = parseDecimal(value)

  try {
    return moneyFormatter(locale, currency).format(numeric)
  } catch {
    return `$${numeric.toFixed(2)}`
  }
}

export function formatDateTime(value: string | Date | null | undefined, config: FormatDateTimeOptions = {}) {
  if (!value) return "—"

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return "—"

  const locale = config.locale ?? intlLocale()
  const baseOptions = config.options ?? {
    dateStyle: config.dateStyle ?? "medium",
    timeStyle: config.timeStyle ?? "short",
  }
  const options = config.timeZone ? { ...baseOptions, timeZone: config.timeZone } : baseOptions

  try {
    return dateTimeFormatter(locale, options).format(date)
  } catch {
    // An invalid time zone must not break rendering; fall back to the browser's zone.
    return dateTimeFormatter(locale, baseOptions).format(date)
  }
}

export function formatDate(value: string | Date | null | undefined, options: Intl.DateTimeFormatOptions = {}) {
  return formatDateTime(value, {
    options: {
      year: "numeric",
      month: "short",
      day: "numeric",
      ...options,
    },
  })
}

type FormatNumberOptions = {
  decimals?: number
  locale?: string
}

export function formatNumber(value: number | string, options: FormatNumberOptions = {}) {
  const { decimals, locale = intlLocale() } = options

  return numberFormatter(locale, decimals).format(parseDecimal(value))
}

/**
 * A percentage the backend already expresses in percentage points — a 5% fee
 * arrives as `5`, not `0.05`. `Intl`'s own percent style multiplies by a
 * hundred, which would print that fee as 500%.
 */
export function formatPercent(value: number | string, options: FormatNumberOptions = {}) {
  return `${formatNumber(value, options)}%`
}

export function parseDecimal(value: number | string | null | undefined) {
  if (value === null || value === undefined) return 0

  const numeric = typeof value === "string" ? Number(value) : value
  return Number.isFinite(numeric) ? numeric : 0
}

function moneyFormatter(locale: string, currency: string) {
  const key = `${locale}:${currency}`
  const cached = moneyFormatters.get(key)
  if (cached) return cached

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  moneyFormatters.set(key, formatter)
  return formatter
}

function numberFormatter(locale: string, decimals?: number) {
  const key = `${locale}:${decimals ?? "auto"}`
  const cached = numberFormatters.get(key)
  if (cached) return cached

  const formatter = new Intl.NumberFormat(
    locale,
    decimals === undefined
      ? {}
      : { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
  )
  numberFormatters.set(key, formatter)
  return formatter
}

function dateTimeFormatter(locale: string, options: Intl.DateTimeFormatOptions) {
  const key = `${locale}:${JSON.stringify(options)}`
  const cached = dateTimeFormatters.get(key)
  if (cached) return cached

  const formatter = new Intl.DateTimeFormat(locale, options)
  dateTimeFormatters.set(key, formatter)
  return formatter
}
